/**
 * Typed meeting API functions using Eden Treaty.
 *
 * All functions accept the company-scoped API client returned by useCompanyApi().
 * Usage:
 *   const company = useCompanyApi()
 *   const { items, nextCursor } = await fetchMeetingsPage(company)
 */

import type {
  IReportArtifactMeta,
  IReportFit,
  TDiarizationProvider,
  TEnsembleMergeMode,
  TStreamChannel,
  TTranscriptionMode,
  TTranscriptionProvider,
} from "@lexicanon/shared"
import type { CompanyApi } from "../../hooks/useCompanyApi.ts"
import { reportFailedResponse } from "../client-error-reporting.ts"
import { apiUrl } from "../runtime-origin.ts"
import type { ISeries } from "../types/template.ts"
import { api } from "./client.ts"
import type { IEnvelopeResult } from "./errors.ts"
import { extractErrorMessage, unwrap, unwrapData } from "./errors.ts"

/**
 * Raw multipart upload helper. Eden Treaty can't reliably serialise `t.File()`
 * bodies, so binary uploads (recording audio, delta chunks, character images)
 * go through plain `fetch` + FormData. This centralises the shared plumbing:
 * it resolves the active org from `window.__activeOrgId`, prefixes the
 * company-scoped route, POSTs the already-built form, and throws on a non-2xx
 * response. Callers build their own FormData (field names + extra fields differ)
 * and pass the company-relative `path` (everything after
 * `/api/companies/:companyId`).
 *
 * @param path        company-relative route, e.g. `/meetings/123/audio`
 * @param form        the multipart body the caller assembled
 * @param errorLabel  prefix for the thrown error message (default `"Upload"`)
 */
async function uploadFormData<TResult>(path: string, form: FormData, errorLabel = "Upload"): Promise<TResult> {
  const companyId = window.__activeOrgId
  const res = await fetch(apiUrl(`/api/companies/${companyId}${path}`), { method: "POST", body: form })
  if (!res.ok) {
    reportFailedResponse(res)
    throw new Error(`${errorLabel} failed: ${res.status}`)
  }
  return res.json() as Promise<TResult>
}

// ---------------------------------------------------------------------------
// Meetings
// ---------------------------------------------------------------------------

/**
 * One cursor-paginated page of the meeting list. `cursor` is the opaque
 * `nextCursor` from the previous page (omit for the first page); `limit` is the
 * page size (server clamps 1..100). Returns `{ items, nextCursor }` — the
 * infinite query on the dashboard chains pages via `nextCursor`.
 */
export async function fetchMeetingsPage(
  company: CompanyApi,
  opts: { showHidden?: boolean; cursor?: string | null; limit?: number; status?: "error" } = {},
) {
  return unwrapData(
    await company.meetings.get({
      query: {
        showHidden: opts.showHidden ? "true" : undefined,
        cursor: opts.cursor ?? undefined,
        limit: opts.limit !== undefined ? String(opts.limit) : undefined,
        status: opts.status,
      },
    }),
  )
}

/**
 * Access-scoped list stats for the dashboard cockpit (total meetings, recorded
 * minutes, this-month count). `monthStart` is the client's local month boundary
 * (ISO) so "this month" respects the viewer's timezone.
 */
export async function fetchMeetingsStats(
  company: CompanyApi,
  opts: { showHidden?: boolean; monthStart?: string } = {},
) {
  return unwrapData(
    await company.meetings.stats.get({
      query: {
        showHidden: opts.showHidden ? "true" : undefined,
        monthStart: opts.monthStart,
      },
    }),
  )
}

export async function fetchActionItemsSummary(company: CompanyApi) {
  return unwrapData(await company.meetings["action-items"].summary.get())
}

export async function fetchMeeting(company: CompanyApi, id: string) {
  return unwrapData(await company.meetings({ id }).get())
}

export async function fetchTranscript(company: CompanyApi, id: string) {
  return unwrapData(await company.meetings({ id }).transcript.get())
}

/** Undo AI transcript cleanup — restore the verbatim original from its backup. */
export async function restoreTranscript(company: CompanyApi, id: string) {
  return unwrapData(await company.meetings({ id }).transcript.restore.post({}))
}

export async function startBrowserMeeting(
  company: CompanyApi,
  subject: string,
  templateId?: string,
  clientId?: string,
  seriesId?: string,
) {
  return unwrapData(await company.meetings.post({ subject, templateId, clientId, seriesId }))
}

export async function updateSubject(company: CompanyApi, id: string, subject: string) {
  return unwrapData(await company.meetings({ id }).patch({ subject }))
}

export async function patchAnalysis(company: CompanyApi, id: string, path: string, value: string) {
  return unwrapData(await company.meetings({ id }).analysis.patch({ path, value }))
}

export async function fetchFieldEdits(company: CompanyApi, id: string) {
  return unwrapData(await company.meetings({ id }).edits.get())
}

// --- Legacy name-keyed speaker shims (fallback for meetings without entity ids) ---

export async function renameSpeaker(company: CompanyApi, id: string, from: string, to: string) {
  return unwrapData(await company.meetings({ id }).speakers.post({ from, to }))
}

export async function discardSpeaker(company: CompanyApi, id: string, speaker: string) {
  return unwrapData(await company.meetings({ id }).speakers.discard.post({ speaker }))
}

// --- Entity-id-keyed speaker mutations (the Wave-3 forward write path) ---

/** A rename hit an existing same-name entity — the peer to merge into. */
export interface ISpeakerMergeCandidate {
  entityId: string
  displayName: string
}

/**
 * Result of an entity rename: `ok` on success, else a merge candidate parsed from
 * the 409 body so the caller can offer "merge into X" instead of a dead error.
 */
export type TRenameSpeakerEntityResult = { ok: true } | { ok: false; mergeCandidate: ISpeakerMergeCandidate }

/**
 * Read the `mergeCandidate` off an Eden 409 error envelope (`{ status, value }`)
 * without an `as` cast — a pure structural guard so we don't depend on Eden's
 * error typing. Returns null for any other error shape or status. Exported for
 * unit tests of the rename → merge conflict path.
 */
export function readMergeCandidate(error: unknown): ISpeakerMergeCandidate | null {
  if (error === null || typeof error !== "object") return null
  if (!("status" in error) || error.status !== 409) return null
  if (!("value" in error)) return null
  const value = error.value
  if (value === null || typeof value !== "object" || !("mergeCandidate" in value)) return null
  const mc = value.mergeCandidate
  if (mc === null || typeof mc !== "object" || !("entityId" in mc) || !("displayName" in mc)) return null
  const { entityId, displayName } = mc
  if (typeof entityId !== "string" || typeof displayName !== "string") return null
  return { entityId, displayName }
}

/**
 * Rename a speaker ENTITY (keyed on its `meeting_speakers` id). A name collision
 * returns `{ ok: false, mergeCandidate }` (from the 409 body) rather than throwing,
 * so the caller can offer to merge into the existing speaker.
 */
export async function renameSpeakerEntity(
  company: CompanyApi,
  meetingId: string,
  entityId: string,
  name: string,
): Promise<TRenameSpeakerEntityResult> {
  const { error } = await company.meetings({ id: meetingId }).speakers({ speakerRef: entityId }).patch({ name })
  if (!error) return { ok: true }
  const candidate = readMergeCandidate(error)
  if (candidate) return { ok: false, mergeCandidate: candidate }
  throw new Error(extractErrorMessage(error))
}

/** Merge source entities INTO the target entity (their labels + voiceprints combine). */
export async function mergeSpeakerEntities(
  company: CompanyApi,
  meetingId: string,
  targetEntityId: string,
  sourceEntityIds: string[],
) {
  return unwrapData(
    await company.meetings({ id: meetingId }).speakers({ speakerRef: targetEntityId }).merge.post({ sourceEntityIds }),
  )
}

/** Undo a merge: revive the tombstoned source entity. */
export async function unmergeSpeakerEntity(
  company: CompanyApi,
  meetingId: string,
  sourceEntityId: string,
  labels?: string[],
) {
  return unwrapData(
    await company.meetings({ id: meetingId }).speakers({ speakerRef: sourceEntityId }).unmerge.post({ labels }),
  )
}

/** Discard a speaker ENTITY (keep its lines, detach the name → needs-ID again). */
export async function discardSpeakerEntity(company: CompanyApi, meetingId: string, entityId: string) {
  return unwrapData(await company.meetings({ id: meetingId }).speakers({ speakerRef: entityId }).delete())
}

/** Link a speaker entity to a workspace roster person (saves this voice as a sample). */
export async function linkSpeakerEntity(company: CompanyApi, meetingId: string, entityId: string, speakerId: string) {
  return unwrapData(
    await company.meetings({ id: meetingId }).speakers({ speakerRef: entityId }).link.post({ speakerId }),
  )
}

/** Unlink a speaker entity from its roster person (keeps the meeting-level name). */
export async function unlinkSpeakerEntity(company: CompanyApi, meetingId: string, entityId: string) {
  return unwrapData(await company.meetings({ id: meetingId }).speakers({ speakerRef: entityId }).link.delete())
}

/**
 * "Not them" on a named or confirmed speaker: clear the name, unlink, block
 * re-suggestion, and optionally revoke this meeting's voice sample.
 */
export async function rejectSpeakerEntity(
  company: CompanyApi,
  meetingId: string,
  entityId: string,
  options: { revokeVoice?: boolean } = {},
) {
  return unwrapData(
    await company
      .meetings({ id: meetingId })
      .speakers({ speakerRef: entityId })
      .reject.post({ revokeVoice: options.revokeVoice ?? true }),
  )
}

/**
 * Update a speaker's cosmetic profile (colour / title). Still name-keyed: the
 * route resolves the display name to its entity server-side, so the caller passes
 * the roster key, not the entity id.
 */
export async function patchSpeakerProfile(
  company: CompanyApi,
  meetingId: string,
  speakerName: string,
  profile: { color?: string | null; title?: string | null },
) {
  return unwrapData(
    await company.meetings({ id: meetingId }).speakers({ speakerRef: speakerName }).profile.patch(profile),
  )
}

export async function fetchSpeakerLines(company: CompanyApi, id: string, speaker: string, limit = 5) {
  return unwrapData(
    await company.meetings({ id }).speakers.get({
      query: { speaker, limit: String(limit) },
    }),
  )
}

export async function hideMeeting(company: CompanyApi, id: string) {
  return unwrapData(await company.meetings({ id }).delete())
}

export async function unhideMeeting(company: CompanyApi, id: string) {
  return unwrapData(await company.meetings({ id }).unhide.post({}))
}

export async function deleteMeetingPermanently(company: CompanyApi, id: string) {
  // biome-ignore lint/suspicious/noExplicitAny: Eden Treaty dynamic path
  return unwrapData(await (company.meetings({ id }) as any).permanent.delete()) // eslint-disable-line @typescript-eslint/no-explicit-any
}

export async function reprocessMeeting(
  company: CompanyApi,
  id: string,
  opts?: {
    systemPrompt?: string
    transcriptionBackend?: TTranscriptionProvider
    diarizationProvider?: TDiarizationProvider
    transcriptionMode?: TTranscriptionMode
    mergeMode?: TEnsembleMergeMode
    remerge?: boolean
    fromAlias?: string
    fromAliases?: string[]
    forceImages?: boolean
  },
) {
  return unwrapData(await company.meetings({ id }).reprocess.post(opts ?? {}))
}

/** Convert this meeting between Full and Lite. Does not change the user's default. */
export async function convertMeetingRecordingMode(
  company: CompanyApi,
  id: string,
  target: "full" | "lite",
): Promise<{ templateId: string; processing: boolean }> {
  return unwrapData(await company.meetings({ id })["recording-mode"].post({ target }))
}

/** Pre-click credit estimate for the reprocess dialog (whole credits, never money). */
export async function fetchReprocessEstimate(
  company: CompanyApi,
  id: string,
  query: {
    mode?: "full" | "speakers" | "analysis" | "chapters" | "images"
    transcriptionBackend?: TTranscriptionProvider
    diarizationProvider?: TDiarizationProvider
    transcriptionMode?: TTranscriptionMode
    mergeMode?: TEnsembleMergeMode
    remerge?: boolean
  },
) {
  return unwrapData(await company.meetings({ id }).reprocess.estimate.get({ query }))
}

/**
 * Translate a meeting's analysis to a target locale (Phase 3 of multi-language).
 * Backend caches the result on the meeting row, so subsequent calls for the same
 * meeting+locale are fast (no LLM round-trip).
 */
export async function translateMeetingAnalysis(company: CompanyApi, id: string, locale: string) {
  return unwrapData(await company.meetings({ id }).translate.post({ locale }))
}

/** Stamp the meeting's source language and enqueue a summary reprocess in that language. */
export async function setMeetingLanguage(company: CompanyApi, id: string, language: string) {
  return unwrapData(await company.meetings({ id }).language.patch({ language }))
}

export async function uploadAudio(_company: CompanyApi, id: string, blob: Blob): Promise<unknown> {
  const ext = blob.type.includes("mp4") ? "mp4" : "webm"
  const form = new FormData()
  form.append("audio", blob, `recording.${ext}`)
  return uploadFormData<unknown>(`/meetings/${id}/audio`, form)
}

export async function stopRecording(_company: CompanyApi, id: string, blob: Blob): Promise<unknown> {
  const ext = blob.type.includes("mp4") ? "mp4" : "webm"
  const form = new FormData()
  form.append("audio", blob, `recording.${ext}`)
  return uploadFormData<unknown>(`/meetings/${id}/audio/finalize`, form)
}

/** Non-blocking iOS-playback readiness for the recording at `recordingUrl`
 * (the meeting's `recording_url`, e.g. `/api/companies/.../meetings/:id/audio`).
 * The player polls this so it never hangs on a cold AAC transcode — see
 * `components/Player`. Mirrors the backend `audioStatus` schema; raw fetch
 * (not Eden Treaty) because the Player works from a URL, like the binary audio
 * path it accompanies. */
export interface IAudioStatus {
  status: "ready" | "transcoding" | "absent"
  /** 0..1 transcode progress; 1 when ready, 0 when absent. */
  progress: number
}

export async function fetchAudioStatus(recordingUrl: string, signal?: AbortSignal): Promise<IAudioStatus> {
  const res = await fetch(apiUrl(`${recordingUrl}/status?format=m4a`), { signal })
  if (!res.ok) throw new Error(`audio status ${res.status}`)
  const data: IAudioStatus = await res.json()
  return data
}

/**
 * Server-side state of a channel's uploaded chunk sequence. A crash-recovery
 * replay of a LEGACY (tail-only) IndexedDB record resumes its upload at
 * `chunkCount` — restarting at chunk 0 would atomically replace the recording
 * file with headerless mid-stream data (the 2026-07-02 corruption).
 */
export async function fetchChunkUploadState(
  company: CompanyApi,
  id: string,
  channel: TStreamChannel = "room",
): Promise<{ chunkCount: number; totalBytes: number }> {
  return unwrapData(await company.meetings({ id }).audio.chunks.get({ query: { channel } }))
}

/**
 * Upload a delta audio chunk to the new /audio/chunk endpoint.
 * Only sends NEW chunks since the last confirmed upload (delta model).
 * Returns a receipt with bytesReceived + totalBytes for client-side verification.
 */
export async function uploadChunk(
  id: string,
  blob: Blob,
  chunkIndex: number,
  isFinal: boolean,
  channel: TStreamChannel = "room",
  offsetSec?: number,
): Promise<{ success: boolean; accepted: boolean; chunkIndex: number; bytesReceived: number; totalBytes: number }> {
  const ext = blob.type.includes("mp4") ? "mp4" : "webm"
  const form = new FormData()
  form.append("audio", blob, `chunk_${chunkIndex}.${ext}`)
  form.append("chunkIndex", String(chunkIndex))
  form.append("isFinal", String(isFinal))
  // Only tag non-room channels so the room request stays byte-for-byte identical
  // (and old/legacy callers default to room server-side).
  if (channel !== "room") form.append("channel", channel)
  if (offsetSec !== undefined) form.append("offsetSec", String(offsetSec))
  return uploadFormData<{
    success: boolean
    accepted: boolean
    chunkIndex: number
    bytesReceived: number
    totalBytes: number
  }>(`/meetings/${id}/audio/chunk`, form, "Chunk upload")
}

export async function stopStreaming(company: CompanyApi, id: string) {
  return unwrapData(await company.meetings({ id }).stream.stop.post())
}

export async function sendMeetingHeartbeat(company: CompanyApi, id: string) {
  // biome-ignore lint/suspicious/noExplicitAny: Eden Treaty dynamic path
  return unwrapData(await (company.meetings as any)({ id }).heartbeat.post()) // eslint-disable-line @typescript-eslint/no-explicit-any
}

/** Report the conditional-loopback verdict (did the room mic already carry the
 *  far-end?) for a meeting. Retries a few times with backoff so a transient
 *  failure (e.g. a 503 during node pressure) doesn't silently drop the verdict and
 *  leave the playback gate inert. Best-effort overall — gives up quietly after the
 *  retries; callers still fire-and-forget. */
export async function reportFarEndInMic(company: CompanyApi, id: string, farEndInMic: boolean): Promise<void> {
  const delays = [0, 1000, 3000]
  for (let attempt = 0; attempt < delays.length; attempt++) {
    if (delays[attempt] > 0) await new Promise((r) => setTimeout(r, delays[attempt]))
    try {
      // biome-ignore lint/suspicious/noExplicitAny: Eden Treaty dynamic (hyphenated) path
      const res: any = await (company.meetings as any)({ id })["far-end-in-mic"].post({ farEndInMic }) // eslint-disable-line @typescript-eslint/no-explicit-any
      if (!res?.error) return
    } catch {
      // network/5xx — retry
    }
  }
}

export async function mergeMeetingsPreflight(company: CompanyApi, ids: string[]): Promise<{ hasEdits: string[] }> {
  // biome-ignore lint/suspicious/noExplicitAny: Eden Treaty dynamic path
  return unwrapData(await (company.meetings as any).merge.preflight.post({ ids })) // eslint-disable-line @typescript-eslint/no-explicit-any
}

export async function mergeMeetings(
  company: CompanyApi,
  ids: string[],
  discardEdits: boolean,
): Promise<{ id: string; mergedFrom: string[] }> {
  // biome-ignore lint/suspicious/noExplicitAny: Eden Treaty dynamic path
  return unwrapData(await (company.meetings as any).merge.post({ ids, discardEdits })) // eslint-disable-line @typescript-eslint/no-explicit-any
}

export async function pauseMeeting(company: CompanyApi, id: string) {
  // biome-ignore lint/suspicious/noExplicitAny: Eden Treaty dynamic path
  return unwrapData(await (company.meetings as any)({ id }).pause.post()) // eslint-disable-line @typescript-eslint/no-explicit-any
}

export async function resumeMeeting(company: CompanyApi, id: string) {
  // biome-ignore lint/suspicious/noExplicitAny: Eden Treaty dynamic path
  return unwrapData(await (company.meetings as any)({ id }).resume.post()) // eslint-disable-line @typescript-eslint/no-explicit-any
}

// ---------------------------------------------------------------------------
// Audit
// ---------------------------------------------------------------------------

export async function fetchAuditLog(company: CompanyApi, limit = 200, offset = 0) {
  return unwrapData(
    await company.audit.get({
      query: { limit: String(limit), offset: String(offset) },
    }),
  )
}

// ---------------------------------------------------------------------------
// Settings
// ---------------------------------------------------------------------------

export async function fetchSettings(company: CompanyApi) {
  return unwrapData(await company.settings.get())
}

export async function updateSettings(company: CompanyApi, settings: Record<string, string>) {
  return unwrapData(await company.settings.put(settings))
}

export async function testLLMProvider(company: CompanyApi, provider: string, apiKey: string) {
  return unwrapData(await company.settings["test-llm"].post({ provider, apiKey }))
}

export async function getAvailableModels(company: CompanyApi) {
  return unwrapData(await company.settings.models.get())
}

export async function fetchImageModels(company: CompanyApi) {
  return unwrapData(await company.settings["image-models"].get())
}

export async function fetchSummaryLayout(company: CompanyApi) {
  return unwrapData(await company.settings["summary-layout"].get())
}

export async function saveSummaryLayout(
  company: CompanyApi,
  blocks: import("@lexicanon/shared").ISummaryBlockOverride[],
) {
  return unwrapData(await company.settings["summary-layout"].put({ blocks }))
}

/**
 * One per-org report-type override the Report Types admin panel submits. Only
 * `typeId` is required; every other field is optional and, when omitted, the
 * backend preserves the org's existing value (or inherits the platform builtin
 * via WS0's `inheritStr` when a prompt/label is blank). Editing surfaces
 * `enabled` / both prompts / `threshold`; `label` / `hints` are reserved for the
 * super-admin platform CRUD.
 */
export interface IReportTypeOverride {
  typeId: string
  enabled?: boolean
  label?: string
  fitPrompt?: string
  generationPrompt?: string
  threshold?: number
  hints?: import("@lexicanon/shared").IReportTypeHints
}

/** The effective (resolved) report types for the active company + the best-model gate + which model it uses. */
export async function fetchReportTypes(company: CompanyApi): Promise<{
  types: import("@lexicanon/shared").IReportTypeDef[]
  bestModelEnabled: boolean
  bestModel: string
  bestModelOptions: { id: string; name: string }[]
}> {
  return unwrapData(await company.settings["report-types"].get())
}

/**
 * Persist a batch of per-org report-type overrides (enable/disable + edited
 * prompts/threshold), and optionally the workspace best-model gate + chosen model.
 */
export async function updateReportTypes(
  company: CompanyApi,
  overrides: IReportTypeOverride[],
  opts?: { bestModelEnabled?: boolean; bestModel?: string },
) {
  const body: { overrides: IReportTypeOverride[]; bestModelEnabled?: boolean; bestModel?: string } = { overrides }
  if (opts?.bestModelEnabled !== undefined) body.bestModelEnabled = opts.bestModelEnabled
  if (opts?.bestModel !== undefined) body.bestModel = opts.bestModel
  return unwrapData(await company.settings["report-types"].put(body))
}

// Admin-scoped settings (super-admin with no active org)
export async function fetchAdminSettings() {
  return unwrapData(await api.api.admin.settings.get())
}

export async function updateAdminSettings(settings: Record<string, string>) {
  return unwrapData(await api.api.admin.settings.put(settings))
}

/**
 * Remove every workspace's own override for the given setting keys so all
 * tenants follow the platform value (now and for future platform changes).
 * Returns how many org overrides were removed per key.
 */
export async function clearTenantSettingOverrides(keys: string[]): Promise<{ cleared: Record<string, number> }> {
  return unwrapData(await api.api.admin.settings["clear-tenant-overrides"].post({ keys }))
}

/**
 * Platform-scope LLM model list (rate-card + capability metadata), for the
 * super-admin Model selection view which has no active company. Mirrors the
 * company-scoped {@link getAvailableModels} shape (both return enriched models),
 * so the ModelsPanel can feed either mode from one dumb view.
 */
export async function fetchAdminModels() {
  return unwrapData(await api.api.admin.models.get())
}

export async function testAdminLLMProvider(provider: string, apiKey: string) {
  return unwrapData(await api.api.admin.settings["test-llm"].post({ provider, apiKey }))
}

export async function testImageProvider(company: CompanyApi, provider: string, apiKey: string) {
  return unwrapData(await company.settings["test-image"].post({ provider, apiKey }))
}

export async function testTeamsIntegration(company: CompanyApi, tenantId: string, appId: string, secret: string) {
  return unwrapData(await company.settings["test-teams"].post({ tenantId, appId, secret }))
}

export async function testAdminTeamsIntegration(tenantId: string, appId: string, secret: string) {
  return unwrapData(await api.api.admin.settings["test-teams"].post({ tenantId, appId, secret }))
}

// Provider credits — third-party API status + remaining credit (super-admin)
export interface IProviderCreditUpdate {
  manualBalance?: number
  unit?: "usd" | "credits"
  lowThreshold?: number | null
  criticalThreshold?: number | null
  emailAlertsEnabled?: boolean
  alertEmail?: string | null
}

export async function fetchProviderCredits() {
  return unwrapData(await api.api.admin["provider-credits"].get())
}

export async function refreshProviderCredits() {
  return unwrapData(await api.api.admin["provider-credits"].refresh.post())
}

export async function updateProviderCredit(provider: string, body: IProviderCreditUpdate) {
  return unwrapData(await api.api.admin["provider-credits"]({ provider }).put(body))
}

// ---------------------------------------------------------------------------
// Tenant usage overview (super-admin activity stats — no money)
// ---------------------------------------------------------------------------

export async function fetchTenantUsage() {
  return unwrapData(await api.api.admin["tenant-usage"].get())
}

// ---------------------------------------------------------------------------
// Usage
// ---------------------------------------------------------------------------

export async function testTranscriptionProvider(company: CompanyApi, provider: string, key: string) {
  return unwrapData(await company.settings["test-transcription"].post({ provider, key }))
}

export async function patchTranscriptLine(
  company: CompanyApi,
  id: string,
  lineIndex: number,
  originalText: string,
  newText: string,
) {
  return unwrapData(await company.meetings({ id })["transcript-line"].patch({ lineIndex, originalText, newText }))
}

export async function fetchVocab(company: CompanyApi) {
  return unwrapData(await company.settings.vocab.get())
}

/** One listed vocabulary term with usage, score, and rank. Zeros if never heard. */
export interface IVocabTermStats {
  term: string
  source: string
  hits: number
  meetings: number
  corrections: number
  disputes: number
  lastSeenAt: string | null
  firstListedAt: string | null
  score: number
  rank: number
}

/** Ranked usage stats for every listed org vocabulary term. */
export interface IVocabStats {
  meetingsCounted: number
  terms: IVocabTermStats[]
}

export async function fetchVocabStats(company: CompanyApi): Promise<IVocabStats> {
  return unwrapData(await company.settings.vocab.stats.get())
}

export async function addVocabTerms(company: CompanyApi, terms: string[]) {
  return unwrapData(await company.settings.vocab.post({ terms }))
}

export async function removeVocabTerm(company: CompanyApi, term: string) {
  return unwrapData(await company.settings.vocab.delete({ term }))
}

// ---------------------------------------------------------------------------
// Adapters
// ---------------------------------------------------------------------------

export async function fetchAdapters(company: CompanyApi) {
  return unwrapData(await company.adapters.get())
}

export async function fetchStreamingAdapters(company: CompanyApi) {
  return unwrapData(await company.adapters.streaming.get())
}

/** One transcription provider's failover health as seen by the answering pod. */
export interface IAdapterHealth {
  provider: string
  state: "closed" | "open" | "half-open"
  failureCount: number
  consecutiveFailures: number
  lastFailureAt: string | null
  lastSuccessAt: string | null
}

/**
 * Live circuit-breaker health for the transcription providers (org-member
 * visible). Global per-pod state — with 2 replicas it reflects the pod that
 * answered — and carries no tenant data. Providers that haven't run since the
 * pod started are absent from the list; the Provider-order panel renders those
 * as "No data yet".
 */
export async function fetchAdapterCircuitBreaker(company: CompanyApi): Promise<{ providers: IAdapterHealth[] }> {
  return unwrapData(await company.adapters["circuit-breaker"].get())
}

/**
 * Per-provider key resolution for this org's effective context (org BYOK →
 * platform setting → env). Drives the Provider-order panel's "No key — skipped"
 * warnings: the chain resolver silently skips keyless providers, so the page
 * must display the same truth it acts on. Booleans only — never key material.
 */
export async function fetchAdapterKeyStatus(
  company: CompanyApi,
): Promise<{ providers: { name: string; hasKey: boolean }[] }> {
  return unwrapData(await company.adapters["key-status"].get())
}

/**
 * Platform-scope variant for the super-admin settings view (no active company):
 * resolution runs platform setting → env var. Same booleans-only contract.
 */
export async function fetchAdminAdapterKeyStatus(): Promise<{ providers: { name: string; hasKey: boolean }[] }> {
  return unwrapData(await api.api.admin.adapters["key-status"].get())
}

export async function fetchDefaultPrompt(company: CompanyApi): Promise<{ prompt: string }> {
  const settings = unwrapData(await company.settings.get()) as Record<string, string>
  return { prompt: settings.system_prompt ?? "" }
}

// ---------------------------------------------------------------------------
// Admin (not company-scoped — uses the root api client)
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Notifications
// ---------------------------------------------------------------------------

export async function fetchNotifications(company: CompanyApi, unreadOnly = false) {
  return unwrapData(await company.notifications.get({ query: { unreadOnly: unreadOnly ? "true" : undefined } }))
}

export async function fetchNotificationCount(company: CompanyApi) {
  return unwrapData(await company.notifications.count.get())
}

export async function markNotificationRead(company: CompanyApi, id: number) {
  // biome-ignore lint/suspicious/noExplicitAny: Eden Treaty dynamic path segments
  const notifications = company.notifications as any // eslint-disable-line @typescript-eslint/no-explicit-any
  const result = (await notifications({ id: String(id) }).read.post()) as IEnvelopeResult<{ success: boolean }>
  return unwrapData(result)
}

export async function markAllNotificationsRead(company: CompanyApi) {
  return unwrapData(await company.notifications["read-all"].post())
}

export interface INotificationRow {
  id: number
  organization_id: string
  type: string
  severity: string
  title: string
  message: string
  adapter: string | null
  metadata: string | null
  read_at: string | null
  created_at: string
}

/** Platform-scope bell. Super-admin on the admin host; org "" rows only. */
export async function fetchPlatformNotifications(unreadOnly = false): Promise<INotificationRow[]> {
  return unwrapData(await api.api.admin.notifications.get({ query: { unreadOnly: unreadOnly ? "true" : undefined } }))
}

export async function fetchPlatformNotificationCount(): Promise<{ unread: number }> {
  return unwrapData(await api.api.admin.notifications.count.get())
}

export async function markPlatformNotificationRead(id: number): Promise<{ success: boolean }> {
  // biome-ignore lint/suspicious/noExplicitAny: Eden Treaty dynamic path segments
  const notifications = api.api.admin.notifications as any // eslint-disable-line @typescript-eslint/no-explicit-any
  const result = (await notifications({ id: String(id) }).read.post()) as IEnvelopeResult<{ success: boolean }>
  return unwrapData(result)
}

export async function markAllPlatformNotificationsRead(): Promise<{ success: boolean }> {
  return unwrapData(await api.api.admin.notifications["read-all"].post())
}

// ---------------------------------------------------------------------------
// Budget
// ---------------------------------------------------------------------------

export async function fetchBudgets(company: CompanyApi) {
  return unwrapData(await company.budget.get())
}

export async function upsertBudget(
  company: CompanyApi,
  adapter: string,
  monthly_limit_credits: number | null,
  alert_threshold_pct?: number,
) {
  // biome-ignore lint/suspicious/noExplicitAny: Eden Treaty dynamic path segments
  return unwrapData(await (company.budget as any)({ adapter }).put({ monthly_limit_credits, alert_threshold_pct })) // eslint-disable-line @typescript-eslint/no-explicit-any
}

export async function deleteBudgetLimit(company: CompanyApi, adapter: string) {
  // biome-ignore lint/suspicious/noExplicitAny: Eden Treaty dynamic path segments
  return unwrapData(await (company.budget as any)({ adapter }).delete()) // eslint-disable-line @typescript-eslint/no-explicit-any
}

// ---------------------------------------------------------------------------
// Admin (not company-scoped — uses the root api client)
// ---------------------------------------------------------------------------

export async function addCompanyMember(
  companyId: string,
  member: { email: string; password: string; name: string; role?: string },
) {
  return unwrapData(await api.api.admin.companies({ id: companyId }).members.post(member))
}

export async function removeCompanyMember(companyId: string, memberId: string) {
  return unwrapData(await api.api.admin.companies({ id: companyId }).members({ memberId }).delete())
}

// --- Invitations: public accept + admin/tenant management ---

export async function fetchInvitation(id: string) {
  return unwrapData(await api.api.invitations({ id }).get())
}

export interface IAcceptInvitationResult {
  status: number
  success?: boolean
  slug?: string | null
  product?: string
  needsLogin?: boolean
  error?: string
}

/**
 * Accept an invitation: creates the account + sets the session cookie. Uses a
 * credentialed fetch (not the treaty) so the browser stores the returned
 * Set-Cookie before we navigate.
 */
export async function acceptInvitation(
  id: string,
  body: { password: string; name: string },
): Promise<IAcceptInvitationResult> {
  const res = await fetch(apiUrl(`/api/invitations/${id}/accept`), {
    method: "POST",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  })
  const data = (await res.json().catch(() => ({}))) as Omit<IAcceptInvitationResult, "status">
  return { status: res.status, ...data }
}

export type TInviteRole = "member" | "admin" | "owner"

export interface IInvitationSummary {
  id: string
  email: string
  role: string
  status: string
  expiresAt: string
  createdAt: string
}

// Super-admin company-scoped invites (admin portal)
export async function inviteCompanyMember(
  companyId: string,
  body: { email: string; role?: TInviteRole },
): Promise<IInvitationSummary> {
  return unwrapData(await api.api.admin.companies({ id: companyId }).invitations.post(body))
}
export async function listCompanyInvitations(companyId: string): Promise<IInvitationSummary[]> {
  return unwrapData(await api.api.admin.companies({ id: companyId }).invitations.get())
}
export async function cancelCompanyInvitation(companyId: string, invitationId: string) {
  return unwrapData(await api.api.admin.companies({ id: companyId }).invitations({ invitationId }).delete())
}

// Tenant-scoped members + invites (org owners/admins)
export async function fetchOrgMembers(company: CompanyApi) {
  return unwrapData(await company.members.get())
}
export async function removeOrgMember(company: CompanyApi, memberId: string) {
  return unwrapData(await company.members({ memberId }).delete())
}
export async function inviteOrgMember(company: CompanyApi, body: { email: string; role?: TInviteRole }) {
  return unwrapData(await company.invitations.post(body))
}
export async function listOrgInvitations(company: CompanyApi) {
  return unwrapData(await company.invitations.get())
}
export async function cancelOrgInvitation(company: CompanyApi, invitationId: string) {
  return unwrapData(await company.invitations({ invitationId }).delete())
}

/** One row of `GET /companies/:cid/speakers` — identity + voice-sample counts. */
export interface ICompanySpeaker {
  id: string
  company_id: string
  name: string
  title: string | null
  color: string | null
  email: string | null
  created_at: string
  updated_at: string
  recognized_meeting_count: number
  sample_count: number
  last_seen_at: string | null
}

export async function fetchCompanySpeakers(
  company: CompanyApi,
  opts: { q?: string; limit?: number } = {},
): Promise<ICompanySpeaker[]> {
  return unwrapData(
    await company.speakers.get({
      query: {
        q: opts.q,
        limit: opts.limit,
      },
    }),
  )
}

export async function fetchSpeakerEnrollments(company: CompanyApi, speakerId: string) {
  // biome-ignore lint/suspicious/noExplicitAny: Eden Treaty dynamic path segment
  return unwrap(await (company.speakers as any)({ speakerId }).enrollments.get()) // eslint-disable-line @typescript-eslint/no-explicit-any
}

export async function deleteSpeakerEnrollment(company: CompanyApi, speakerId: string, embeddingId: string) {
  // biome-ignore lint/suspicious/noExplicitAny: Eden Treaty dynamic path segments
  return unwrap(await (company.speakers as any)({ speakerId }).enrollments({ embeddingId }).delete()) // eslint-disable-line @typescript-eslint/no-explicit-any
}

export async function applySpeakerHypothesis(
  company: CompanyApi,
  meetingId: string,
  body: { label: string; name: string; action: "confirm" | "reject" },
): Promise<{ success: boolean; status: "unidentified" | "tentative" | "confirmed"; confirmedName: string | null }> {
  return unwrapData(await company.meetings({ id: meetingId }).speakers.hypothesis.post(body))
}

export async function fetchCompanySettings(
  companyId: string,
): Promise<{ platform: Record<string, string>; org: Record<string, string> }> {
  return unwrapData(await api.api.admin.companies({ id: companyId }).settings.get())
}

export async function updateCompanySettings(companyId: string, settings: Record<string, string>) {
  return unwrapData(await api.api.admin.companies({ id: companyId }).settings.put(settings))
}

export async function deleteCompanySettingOverride(companyId: string, key: string) {
  return unwrapData(await api.api.admin.companies({ id: companyId }).settings({ key }).delete())
}

// ---------------------------------------------------------------------------
// Series (campaigns)
// ---------------------------------------------------------------------------

export async function fetchSeries(company: CompanyApi): Promise<ISeries[]> {
  return unwrapData(await company.series.get())
}

export async function fetchSeriesSummary(company: CompanyApi) {
  return unwrapData(await company.series.summary.get())
}

export async function fetchSeriesById(company: CompanyApi, id: string): Promise<ISeries> {
  return unwrapData(await company.series({ id }).get())
}

export async function createSeries(
  company: CompanyApi,
  data: { name: string; description?: string; templateId?: string },
) {
  return unwrapData(await company.series.post(data))
}

export async function updateSeries(
  company: CompanyApi,
  id: string,
  data: { name?: string; description?: string; templateId?: string; metadata?: Record<string, unknown> },
) {
  return unwrapData(await company.series({ id }).patch(data))
}

export async function fetchSeriesMembers(
  company: CompanyApi,
  seriesId: string,
): Promise<{ id: string; name: string; email: string }[]> {
  // Eden dynamic-path — typed end-to-end, org scoped from the session by companyGuard.
  return unwrapData(await company.series({ id: seriesId }).members.get())
}

export async function deleteSeries(company: CompanyApi, id: string) {
  return unwrapData(await company.series({ id }).delete())
}

// ---------------------------------------------------------------------------
// Characters
// ---------------------------------------------------------------------------

/**
 * A Voxcanon series character, mirroring the backend `CharactersModel.characterItem`
 * shape (`GET /series/:id/characters`). This is the single source of truth — the
 * campaigns detail page and the live TTRPG panel both import it (the panel via a
 * `Pick<>` of the three fields it renders) instead of re-declaring divergent copies.
 */
export interface ICharacter {
  id: string
  seriesId: string
  userId: string | null
  name: string
  playerName: string | null
  characterClass: string | null
  backstory: string | null
  playStyle: string | null
  referenceImageUrl: string | null
  avatarUrl: string | null
  fullBodyUrl: string | null
  imagePrompt: string | null
  metadata: Record<string, unknown>
  createdAt: string | null
}

export async function fetchCharacters(company: CompanyApi, seriesId: string): Promise<ICharacter[]> {
  return unwrapData(await company.series({ id: seriesId }).characters.get())
}

export async function createCharacter(
  company: CompanyApi,
  seriesId: string,
  data: {
    name: string
    userId?: string | null
    playerName?: string
    characterClass?: string
    backstory?: string
    playStyle?: string
    referenceImageUrl?: string
    avatarUrl?: string
    fullBodyUrl?: string
    imagePrompt?: string
    metadata?: Record<string, unknown>
  },
) {
  return unwrapData(await company.series({ id: seriesId }).characters.post(data))
}

export async function updateCharacter(
  company: CompanyApi,
  seriesId: string,
  charId: string,
  data: {
    name?: string
    userId?: string | null
    playerName?: string
    characterClass?: string
    backstory?: string
    playStyle?: string
    referenceImageUrl?: string
    avatarUrl?: string
    fullBodyUrl?: string
    imagePrompt?: string
    metadata?: Record<string, unknown>
  },
) {
  return unwrapData(await company.series({ id: seriesId }).characters({ charId }).patch(data))
}

export async function uploadCharacterAvatar(
  _company: CompanyApi,
  seriesId: string,
  charId: string,
  file: File,
): Promise<{ url: string }> {
  const form = new FormData()
  form.append("image", file)
  return uploadFormData<{ url: string }>(`/series/${seriesId}/characters/${charId}/avatar`, form, "Avatar upload")
}

export async function uploadCharacterFullBody(
  _company: CompanyApi,
  seriesId: string,
  charId: string,
  file: File,
): Promise<{ url: string }> {
  const form = new FormData()
  form.append("image", file)
  return uploadFormData<{ url: string }>(`/series/${seriesId}/characters/${charId}/full-body`, form, "Full-body upload")
}

export async function deleteCharacter(company: CompanyApi, seriesId: string, charId: string) {
  return unwrapData(await company.series({ id: seriesId }).characters({ charId }).delete())
}

// ---------------------------------------------------------------------------
// TTRPG Participants
// ---------------------------------------------------------------------------

export interface IParticipant {
  id: string
  userId: string
  characterId: string | null
  role: string
  joinedAt: string | null
}

export async function fetchParticipants(company: CompanyApi, meetingId: string): Promise<IParticipant[]> {
  // biome-ignore lint/suspicious/noExplicitAny: Eden Treaty dynamic path segments
  return unwrapData(await (company.meetings as any)({ id: meetingId }).participants.get()) // eslint-disable-line @typescript-eslint/no-explicit-any
}

export async function joinSession(
  company: CompanyApi,
  meetingId: string,
  role: "dm" | "player" | "observer",
  characterId?: string,
) {
  // biome-ignore lint/suspicious/noExplicitAny: Eden Treaty dynamic path segments
  return unwrapData(await (company.meetings as any)({ id: meetingId }).participants.post({ role, characterId })) // eslint-disable-line @typescript-eslint/no-explicit-any
}

// ---------------------------------------------------------------------------
// TTRPG Visuals & Reports
// ---------------------------------------------------------------------------

/**
 * Rich `content` blob of a meeting visual. The backend stores it as a JSON
 * column and types the response as `Record<string, unknown>`, so the concrete
 * field set can't flow through Eden Treaty — `fetchVisuals` owns the single
 * narrowing cast and every consumer reads these typed fields. Union of the
 * fields produced by the scene / portrait / moment image steps.
 */
export interface IMeetingVisualContent {
  url: string
  prompt?: string
  title?: string
  type?: string
  description?: string
  provider?: string
  charactersInvolved?: string[]
  referenceImages?: string[]
  sceneIndex?: number
  sourceType?: "scene" | "memorable_moment" | "character" | string
  characterName?: string
  role?: string
  highlights?: string
}

/**
 * A generated meeting visual (scene image, character portrait, memorable-moment
 * plaque). Mirrors the backend `ArtifactsModel.visualItem`; the single source of
 * truth replacing the previously divergent `ISceneImage` (SceneInsightsDrawer)
 * and `IVisual` (TTRPGReports / scene viewer) hand-written copies.
 */
export interface IMeetingVisual {
  id: number
  stepId: string
  content: IMeetingVisualContent
  ordinal: number | null
  visibility: string | null
  createdAt: string | null
}

export async function fetchVisuals(
  company: CompanyApi,
  meetingId: string,
  visibleOnly = false,
): Promise<IMeetingVisual[]> {
  // biome-ignore lint/suspicious/noExplicitAny: Eden Treaty dynamic path segments
  const meetingApi = company.meetings as any // eslint-disable-line @typescript-eslint/no-explicit-any
  const rows = unwrapData(
    await meetingApi({ id: meetingId }).visuals.get({ query: visibleOnly ? { visibleOnly: "true" } : {} }),
  )
  // The backend types `content` as `Record<string, unknown>`; this wrapper is the
  // one place that narrows it to the concrete `IMeetingVisualContent` shape so no
  // consumer needs its own blind cast.
  return rows as IMeetingVisual[]
}

export async function revealArtifact(
  company: CompanyApi,
  meetingId: string,
  artifactId: number,
  visibility: "dm_only" | "all" | "hidden",
) {
  // biome-ignore lint/suspicious/noExplicitAny: Eden Treaty dynamic path segments
  const meetingApi = company.meetings as any // eslint-disable-line @typescript-eslint/no-explicit-any
  return unwrapData(
    await meetingApi({ id: meetingId })
      .visuals({ artifactId: String(artifactId) })
      .patch({ visibility }),
  )
}

/**
 * Post-session DM coaching report. The backend stores it as a JSON blob typed
 * `Record<string, unknown>`, so the concrete fields are narrowed here in the
 * single typed wrapper rather than at each consumer.
 */
export interface IDMReport {
  overall_rating: string
  pacing: string
  engagement_balance: {
    most_active: string
    least_active: string
    note: string
  }
  storytelling: string
  strengths: string[]
  suggestions: string[]
}

/** Per-player post-session report (one per participant). */
export interface IPlayerReport {
  userId: string
  characterId: string | null
  roleplay_rating: string
  highlights: string[]
  missed_opportunities: string[]
  character_consistency: string
  suggestions: string[]
}

/** Cumulative character arc across the campaign so far. */
export interface ICharacterArc {
  characterId: string
  characterName: string
  userId: string
  arc_progression: string
  relationships: Array<{ with: string; change: string }>
  growth_moments: string[]
  cumulative_summary: string
}

/** Aggregate of all post-session reports returned by `GET /visuals/reports`. */
export interface IReports {
  dmReport: IDMReport | null
  playerReports: IPlayerReport[]
  characterArcs: ICharacterArc[]
}

export async function fetchReports(company: CompanyApi, meetingId: string): Promise<IReports> {
  const reports = unwrapData(
    // biome-ignore lint/suspicious/noExplicitAny: Eden Treaty dynamic path segments
    await (company.meetings as any)({ id: meetingId }).visuals.reports.get(), // eslint-disable-line @typescript-eslint/no-explicit-any
  )
  // Backend types the report blobs as `Record<string, unknown>`; this wrapper is
  // the single place that narrows them to the concrete report shapes.
  return reports as IReports
}

// ---------------------------------------------------------------------------
// Visual reports (Lexicanon meeting-intelligence) — the offer banner + viewer.
// Company-scoped under /meetings/:id/visuals/(report-fit|visual-reports). The
// `visuals` subtree indexes dynamic path params, so — like fetchVisuals above —
// the client is stepped through `as any` and each response reshaped via the
// `IUnwrapped<T>` cast documented in errors.ts.
// ---------------------------------------------------------------------------

/** One already-generated visual report (id + its stored metadata). */
export interface IVisualReportListItem {
  id: number
  meta: IReportArtifactMeta
  createdAt: string | null
}

/**
 * Report-fit suggestion for a meeting. The backend returns a cached fit when one
 * exists, else computes + caches it lazily (the old-meeting first-view path), so
 * this single GET drives the offer banner's whole "suggested" state.
 */
export async function fetchReportFit(company: CompanyApi, meetingId: string): Promise<IReportFit> {
  // biome-ignore lint/suspicious/noExplicitAny: Eden Treaty dynamic path segments
  const meetingApi = company.meetings as any // eslint-disable-line @typescript-eslint/no-explicit-any
  const result = (await meetingApi({ id: meetingId }).visuals["report-fit"].get()) as IEnvelopeResult<IReportFit>
  return unwrapData(result)
}

/** The visual reports already generated for a meeting (which types exist + their metadata). */
export async function fetchVisualReports(company: CompanyApi, meetingId: string): Promise<IVisualReportListItem[]> {
  // biome-ignore lint/suspicious/noExplicitAny: Eden Treaty dynamic path segments
  const meetingApi = company.meetings as any // eslint-disable-line @typescript-eslint/no-explicit-any
  const result = (await meetingApi({ id: meetingId }).visuals["visual-reports"].get()) as IEnvelopeResult<
    IVisualReportListItem[]
  >
  return unwrapData(result)
}

/**
 * START generating one visual report of `type` on-demand. Fire-and-forget: the
 * backend runs generation in a detached task (minutes) and returns a small ack
 * immediately, then emits `report_generation_*` SSE events. The caller tracks
 * progress via those events (see MeetingReportBlock), not this reply. Metering is
 * automatic server-side.
 */
export async function generateVisualReport(
  company: CompanyApi,
  meetingId: string,
  type: string,
  tier: "standard" | "best" = "standard",
): Promise<{ started: boolean }> {
  // biome-ignore lint/suspicious/noExplicitAny: Eden Treaty dynamic path segments
  const meetingApi = company.meetings as any // eslint-disable-line @typescript-eslint/no-explicit-any
  const result = (await meetingApi({ id: meetingId }).visuals["visual-reports"].post({
    type,
    tier,
  })) as IEnvelopeResult<{
    started: boolean
  }>
  return unwrapData(result)
}

/** Pre-click cost estimate per model tier in CREDITS (real markup + peg applied) + whether the best tier is offered. */
export interface IReportEstimate {
  standard: { credits: number }
  best: { credits: number }
  bestAllowed: boolean
}

/** Pre-click cost estimate for generating a report of `type`, for both model tiers. */
export async function fetchReportEstimate(
  company: CompanyApi,
  meetingId: string,
  type: string,
): Promise<IReportEstimate> {
  // biome-ignore lint/suspicious/noExplicitAny: Eden Treaty dynamic path segments
  const meetingApi = company.meetings as any // eslint-disable-line @typescript-eslint/no-explicit-any
  const result = (await meetingApi({ id: meetingId }).visuals["visual-reports"].estimate.get({
    query: { type },
  })) as IEnvelopeResult<IReportEstimate>
  return unwrapData(result)
}

/**
 * Build the tenant-scoped URL of a generated report's self-contained HTML — the
 * `<iframe src>` for ReportViewer, the `?download=1` variant for an HTML file
 * save, or `?format=pdf` for the Chromium-printed PDF (always an attachment).
 * A plain URL (not an Eden call) because the endpoint serves raw HTML the iframe
 * navigates to directly (cookies ride the same-origin request).
 */
export function visualReportHtmlUrl(
  companyId: string,
  meetingId: string,
  artifactId: number,
  opts?: { download?: boolean; format?: "pdf" },
): string {
  const base = apiUrl(
    `/api/companies/${encodeURIComponent(companyId)}/meetings/${encodeURIComponent(meetingId)}/visuals/visual-reports/${artifactId}/html`,
  )
  const params = new URLSearchParams()
  if (opts?.download) params.set("download", "1")
  if (opts?.format) params.set("format", opts.format)
  const qs = params.toString()
  return qs ? `${base}?${qs}` : base
}
